import { Lock, Crown, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

/**
 * LockedFeature — overlay/card shown when a user does not have access to a feature.
 *
 * Usage:
 *   <LockedFeature requiredTier="advance" featureName="Adaptive Learning" />
 *   <LockedFeature requiredTier="pro"     featureName="Tutor Papers" />
 */
const TIER_META = {
  beginner: { label: 'Beginner', color: 'from-sky-400 to-sky-600', icon: Sparkles },
  advance:  { label: 'Advance',  color: 'from-purple-500 to-fuchsia-600', icon: Sparkles },
  pro:      { label: 'Pro',      color: 'from-amber-400 to-yellow-600', icon: Crown },
};

export const LockedFeature = ({
  requiredTier = 'advance',
  featureName = 'This feature',
  compact = false,
}) => {
  const navigate = useNavigate();
  const meta = TIER_META[requiredTier] || TIER_META.advance;
  const Icon = meta.icon;

  if (compact) {
    return (
      <div
        className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-sm"
        data-testid={`locked-feature-compact-${requiredTier}`}
      >
        <Lock className="h-4 w-4 text-gray-500" />
        <span className="text-gray-700">{featureName}</span>
        <Badge className={`ml-auto bg-gradient-to-r ${meta.color} text-white`}>
          {meta.label}+
        </Badge>
      </div>
    );
  }

  return (
    <Card
      className="relative overflow-hidden border-2 border-dashed border-gray-300"
      data-testid={`locked-feature-${requiredTier}`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${meta.color} opacity-5 pointer-events-none`} />
      <CardContent className="relative p-6 text-center space-y-4">
        <div className={`mx-auto w-14 h-14 rounded-full bg-gradient-to-br ${meta.color} flex items-center justify-center shadow-lg`}>
          <Lock className="h-7 w-7 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">{featureName} is a {meta.label} feature</h3>
          <p className="text-sm text-gray-600 mt-1">
            Upgrade to <span className="font-semibold">{meta.label}</span> to unlock this and more.
          </p>
        </div>
        <Button
          onClick={() => navigate('/subscription')}
          className={`bg-gradient-to-r ${meta.color} text-white hover:opacity-90`}
          data-testid={`upgrade-to-${requiredTier}-btn`}
        >
          <Icon className="h-4 w-4 mr-2" />
          Upgrade to {meta.label}
        </Button>
      </CardContent>
    </Card>
  );
};

export default LockedFeature;
